import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Download, TrendingUp, Euro, CalendarCheck } from "lucide-react";

const COLORS = ["#17B8B0", "#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444"];
const now = new Date();
const YEARS = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
const MONTHS = [["all", "Tutto l'anno"], ...Array.from({ length: 12 }, (_, i) => [String(i + 1), new Date(2000, i, 1).toLocaleString("it-IT", { month: "long" })])];

export default function Analytics() {
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (month !== "all") params.month = month;
      const res = await api.get("/analytics/summary", { params });
      setData(res.data);
    } catch { toast.error("Errore caricamento analisi"); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    try {
      const params = { year };
      if (month !== "all") params.month = month;
      const res = await api.get("/analytics/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `PT-MED_Analisi_${year}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel scaricato");
    } catch { toast.error("Errore export"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Analisi" subtitle="Andamento fatturato, trattamenti e carico collaboratori">
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-[110px] bg-white" data-testid="filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[150px] bg-white capitalize" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map(([v, l]) => <SelectItem key={v} value={v} className="capitalize">{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={exportExcel} data-testid="btn-export-excel" className="font-semibold">
            <Download className="mr-1.5 h-4 w-4" /> Export Excel
          </Button>
        </div>
      </PageHeader>

      {!data ? (
        <div className="text-sm text-muted-foreground py-8">Caricamento...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs uppercase tracking-wider text-slate-500">Incasso totale</p>
                  <p className="mt-2 font-heading text-3xl font-bold font-mono-data">€ {data.totale_incasso.toLocaleString("it-IT")}</p></div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-100"><Euro className="h-5 w-5 text-emerald-600" /></div>
              </div>
            </Card>
            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs uppercase tracking-wider text-slate-500">Appuntamenti</p>
                  <p className="mt-2 font-heading text-3xl font-bold font-mono-data">{data.totale_appuntamenti}</p></div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10"><CalendarCheck className="h-5 w-5 text-primary" /></div>
              </div>
            </Card>
            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between">
                <div><p className="text-xs uppercase tracking-wider text-slate-500">Servizi diversi</p>
                  <p className="mt-2 font-heading text-3xl font-bold font-mono-data">{data.servizi.length}</p></div>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100"><TrendingUp className="h-5 w-5 text-blue-600" /></div>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
              <h3 className="font-heading font-semibold text-slate-800 mb-4">Andamento incassi</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="mese" fontSize={12} stroke="#94a3b8" />
                  <YAxis fontSize={12} stroke="#94a3b8" />
                  <Tooltip formatter={(v) => `€ ${v}`} />
                  <Line type="monotone" dataKey="incasso" stroke="#17B8B0" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm">
              <h3 className="font-heading font-semibold text-slate-800 mb-4">Servizi più richiesti</h3>
              {data.servizi.length === 0 ? (
                <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={data.servizi} dataKey="sedute" nameKey="servizio" cx="50%" cy="50%" outerRadius={90} label>
                      {data.servizi.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card className="p-5 rounded-xl border-slate-200 bg-white shadow-sm lg:col-span-2">
              <h3 className="font-heading font-semibold text-slate-800 mb-4">Carico collaboratori (ore)</h3>
              {data.collaboratori.length === 0 ? (
                <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.collaboratori}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                    <XAxis dataKey="collaboratore" fontSize={12} stroke="#94a3b8" />
                    <YAxis fontSize={12} stroke="#94a3b8" />
                    <Tooltip formatter={(v) => `${v} h`} />
                    <Bar dataKey="ore" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
